import { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Users,
  UserPlus,
  Settings,
  CheckCircle,
  Plus,
  Archive,
  Package,
  Building2,
} from "lucide-react";

import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { ArchivedCount, MetricCard } from "../../components/MetricCard";
import { Badge } from "../../components/Badge";
import { DataTable } from "../../components/DataTable";
import { Card, CardContent } from "../../components/Card";
import { AgencyCard } from "./AgencyCard";
import { TeamMemberCard } from "./TeamMemberCard";
import { MemberModal } from "./MemberModal";
import { AgencyModal } from "./AgencyModal";
import { AgencyBrandingSettings } from "../../components/AgencyBrandingSettings";
import { AssignAgencyModal } from "./AssignAgencyModal";
import { InviteUserModal } from "./InviteUserModal";
import { PlanLimitDialog } from "../../components/PlanLimitDialog";
import { getPlanLimitError } from "../../utils/planLimits";
import { useAuth } from "../../components/contexts/AuthContext";
import { useTheme } from "../../theme/useTheme";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client.js";
import SuccessPopup from "../../components/SuccessPopup";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import { InfoTooltip } from "../../components/InfoTooltip";
import { getAvatarImageUrl } from "../../utils/avatarUtils";

const PACK_BREAKDOWN_ITEMS = [
  { key: "local", label: "Starter" },
  { key: "basic", label: "Basic" },
  { key: "ecommerce", label: "E-commerce" },
  { key: "premium", label: "Premium" },
  { key: "infoproducto", label: "Infoproducto" },
  { key: "sin_pack", label: "Sin pack" },
];

const PACK_LABEL_BY_KEY = PACK_BREAKDOWN_ITEMS.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});

const PACK_COLOR_BY_KEY = {
  local: "blue",
  basic: "orange",
  ecommerce: "green",
  premium: "purple",
  infoproducto: "yellow",
  sin_pack: "destructive",
};

const formatClientJoinDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const normalizeAgencyClientStats = (stats = {}) => {
  const sourceByPack =
    stats.by_pack ||
    stats.byPack ||
    stats.pack_counts ||
    stats.clients_by_pack ||
    {};

  const byPack = PACK_BREAKDOWN_ITEMS.reduce((acc, item) => {
    acc[item.key] = toNumber(sourceByPack[item.key]);
    return acc;
  }, {});

  byPack.sin_pack = toNumber(
    sourceByPack.sin_pack ?? sourceByPack.otros ?? sourceByPack.other
  );

  const total = toNumber(
    stats.total ?? stats.all ?? stats.total_clients ?? stats.client_count
  );
  const archived = toNumber(
    stats.archived ?? stats.archived_clients ?? stats.archived_count
  );
  const active = toNumber(
    stats.active ?? stats.active_clients ?? stats.active_count ?? total - archived
  );

  return {
    total,
    active,
    archived,
    byPack,
  };
};

const formatAgencyForDisplay = (agency) => {
  return {
    id: agency.id,
    name: agency.name || "Sin nombre",
    location: agency.location || "",
    slug: agency.slug || "",
    email: agency.email || "",
    phone: agency.phone || "",
    website: agency.website || "",
    additional_notes: agency.additional_notes || "",
    clientStats: normalizeAgencyClientStats(agency.client_stats),
    agencyType: agency.agency_type || "serseo",
    subscription: agency.subscription || null,
    isArchived: agency.is_archived,
  };
};

const AgencyClientStatsModal = ({ agency, open, onClose }) => {
  const { theme } = useTheme();
  const [displayAgency, setDisplayAgency] = useState(agency);

  useEffect(() => {
    if (agency) {
      setDisplayAgency(agency);
    }
  }, [agency]);

  const agencyClientsEndpoint = displayAgency?.id
    ? `clients/?agency=${displayAgency.id}&archived=all&page_size=100&ordering=name`
    : null;

  const {
    data: agencyClientsData,
    isLoading: agencyClientsLoading,
  } = getData(
    agencyClientsEndpoint,
    open && Boolean(displayAgency?.id),
    [displayAgency?.id, open]
  );

  const agencyClients = useMemo(() => {
    if (Array.isArray(agencyClientsData)) return agencyClientsData;
    if (Array.isArray(agencyClientsData?.results)) return agencyClientsData.results;
    return [];
  }, [agencyClientsData]);

  const stats = displayAgency?.clientStats || normalizeAgencyClientStats();
  const totalClients = Math.max(stats.total, 0);
  const activeClients = Math.max(stats.active, 0);
  const archivedClients = Math.max(stats.archived, 0);
  const summaryItems = [
    {
      label: "Total",
      value: totalClients,
      icon: Users,
      color: theme.palette.primary.main,
    },
    {
      label: "Activos",
      value: activeClients,
      icon: CheckCircle,
      color: theme.palette.success.main,
    },
    {
      label: "Archivados",
      value: archivedClients,
      icon: Archive,
      color: theme.palette.warning.main,
    },
  ];

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={
        displayAgency
          ? `Clientes de ${displayAgency.name}`
          : "Detalle de clientes"
      }
      maxWidth="lg"
    >
      <Stack spacing={3}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {summaryItems.map((item) => {
            const Icon = item.icon;

            return (
              <Box
                key={item.label}
                sx={{
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    minHeight: 112,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: item.color,
                        backgroundColor: alpha(item.color, 0.1),
                      }}
                    >
                      <Icon size={18} />
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.03em",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.8rem", sm: "2rem" },
                        lineHeight: 1,
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Divider />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 240px" },
            gap: 3,
            alignItems: "start",
          }}
        >
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Users size={16} color={theme.palette.text.secondary} />
            <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
              Todos los clientes ({agencyClients.length})
            </Typography>
          </Box>

          <DataTable
            size="sm"
            maxHeight={260}
            minHeight={260}
            rows={agencyClients}
            loading={agencyClientsLoading}
            skeletonRows={3}
            emptyContent={
              <Typography
                align="center"
                sx={{ color: theme.palette.text.secondary, py: 3, fontSize: "0.875rem" }}
              >
                Esta agencia no tiene clientes todavía
              </Typography>
            }
            columns={[
              {
                key: "name",
                header: "Cliente",
                cellSx: { fontWeight: 500 },
                render: (client) => (
                  <>
                    {client.name}
                    {client.is_archived && (
                      <Badge
                        size="sm"
                        sx={{
                          ml: 1,
                          backgroundColor: theme.palette.error.main,
                          color: theme.palette.common.white,
                          border: `1px solid ${theme.palette.error.main}`,
                        }}
                      >
                        Archivado
                      </Badge>
                    )}
                  </>
                ),
              },
              {
                key: "pack",
                header: "Pack",
                render: (client) => {
                  const packKey = client.setup_project_info?.setup_variant || "sin_pack";
                  return (
                    <Badge
                      size="sm"
                      color={PACK_COLOR_BY_KEY[packKey]}
                      sx={
                        packKey === "sin_pack"
                          ? {
                              backgroundColor: "transparent",
                              color: "text.secondary",
                              border: `1px solid ${theme.palette.divider}`,
                            }
                          : undefined
                      }
                    >
                      {PACK_LABEL_BY_KEY[packKey]}
                    </Badge>
                  );
                },
              },
              {
                key: "created_at",
                header: "Fecha de alta",
                render: (client) => formatClientJoinDate(client.created_at),
              },
            ]}
          />
        </Stack>

        <Stack
          spacing={1}
          sx={{
            minWidth: 0,
            pl: { xs: 0, md: 2 },
            borderLeft: { xs: "none", md: `1px solid ${theme.palette.divider}` },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Package size={16} color={theme.palette.text.secondary} />
            <InfoTooltip
              title="Solo se contabilizan los clientes activos (no archivados)."
              zIndex={100001}
            >
              <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                Resumen por pack
              </Typography>
            </InfoTooltip>
          </Box>

          <DataTable
            size="sm"
            maxHeight={260}
            minHeight={260}
            tableSx={{ tableLayout: "fixed" }}
            rows={PACK_BREAKDOWN_ITEMS}
            getRowId={(item) => item.key}
            columns={[
              {
                key: "label",
                header: "Pack",
                width: "65%",
                cellSx: { fontWeight: 500 },
                render: (item) => item.label,
              },
              {
                key: "count",
                header: "Clientes",
                align: "right",
                width: "35%",
                cellSx: { fontWeight: 700 },
                render: (item) => stats.byPack[item.key] || 0,
              },
            ]}
          />
        </Stack>
        </Box>
      </Stack>
    </UnifiedModal>
  );
};

AgencyClientStatsModal.propTypes = {
  agency: PropTypes.shape({
    name: PropTypes.string,
    clientStats: PropTypes.shape({
      total: PropTypes.number,
      active: PropTypes.number,
      archived: PropTypes.number,
      byPack: PropTypes.objectOf(PropTypes.number),
    }),
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const getPrimaryAgencyId = (user) => {
  if (user?.agency && typeof user.agency === "object" && user.agency.id) {
    return user.agency.id;
  }
  if (typeof user?.agency === "number") {
    return user.agency;
  }
  if (Array.isArray(user?.agencies) && user.agencies.length > 0) {
    return user.agencies[0].id;
  }
  return null;
};

const formatUserForDisplay = (user, agencyMap, agencyContext = null) => {
  const agencies = [];

  if (user.agency) {
    if (typeof user.agency === "object" && user.agency.id) {
      agencies.push({ id: user.agency.id, name: user.agency.name });
    } else if (typeof user.agency === "number") {
      const agencyData = agencyMap.get(user.agency);
      const contextMatch =
        agencyContext?.id === user.agency ? agencyContext : null;
      agencies.push({
        id: user.agency,
        name: agencyData?.name || contextMatch?.name || `Agency ${user.agency}`,
      });
    }
  }

  if (Array.isArray(user.invited_agencies)) {
    user.invited_agencies.forEach((invitedAgency) => {
      if (!agencies.some((agency) => agency.id === invitedAgency.id)) {
        agencies.push({
          id: invitedAgency.id,
          name: invitedAgency.name,
        });
      }
    });
  }

  if (agencies.length === 0 && Array.isArray(user.agencies)) {
    agencies.push(...user.agencies);
  }

  const primaryAgencyId = getPrimaryAgencyId(user);
  const firstName = user.first_name || "";
  const lastName = user.last_name || "";
  const displayName = `${firstName} ${lastName}`.trim();

  return {
    id: user.id,
    name: displayName || user.email,
    email: user.email,
    role: user.role === "agency_admin" ? "Admin de Agencia" : "Consultor",
    agencies: agencies,
    agencyId: primaryAgencyId,
    phone: user.telephone || "",
    location: "",
    joinDate: user.created_at,
    status: user.is_active ? "active" : "inactive",
    profile_image: getAvatarImageUrl(user.profile_image),
    avatar: `${firstName.charAt(0)}${lastName.charAt(0)}`,
    stats: {
      totalClients: user.client_count || 0,
    },
    client_names: Array.isArray(user.client_names) ? user.client_names : [],
    isMultiAgency: agencies.length > 1,
  };
};

const buildAgencyMap = (agencies) => {
  const map = new Map();
  agencies.forEach((agency) => {
    if (agency?.id != null) {
      map.set(agency.id, agency);
    }
  });
  return map;
};

const normalizeTeamData = (apiData, currentUserRole) => {
  if (!apiData) {
    return { agencies: [], users: [] };
  }

  const processedAgencies = [];
  const processedUsers = [];
  const seenUserIds = new Set();
  const seenAgencyIds = new Set();

  const addAgency = (agency) => {
    if (!agency?.id || seenAgencyIds.has(agency.id)) return;
    seenAgencyIds.add(agency.id);
    processedAgencies.push(formatAgencyForDisplay(agency));
  };

  if (currentUserRole === "superadmin") {
    const agenciesSource = Array.isArray(apiData)
      ? apiData
      : apiData?.id
        ? [apiData]
        : [];
    const agencyMap = buildAgencyMap(agenciesSource);

    agenciesSource.forEach((agency) => {
      addAgency(agency);
      const workers = Array.isArray(agency.workers) ? agency.workers : [];
      workers.forEach((worker) => {
        const formattedUser = formatUserForDisplay(
          worker,
          agencyMap,
          { id: agency.id, name: agency.name }
        );
        if (!seenUserIds.has(formattedUser.id)) {
          seenUserIds.add(formattedUser.id);
          processedUsers.push(formattedUser);
        }
      });
    });

    return { agencies: processedAgencies, users: processedUsers };
  }

  if (currentUserRole === "agency_admin") {
    const groups = Array.isArray(apiData.groups) ? apiData.groups : [];
    const agenciesFromGroups = groups
      .map((group) => group.agency)
      .filter(Boolean);
    const agencyMap = buildAgencyMap(agenciesFromGroups);

    groups.forEach((group) => {
      if (group.agency) {
        addAgency(group.agency);
      }
      const groupUsers = Array.isArray(group.users) ? group.users : [];
      groupUsers.forEach((user) => {
        const formattedUser = formatUserForDisplay(
          user,
          agencyMap,
          group.agency
        );
        if (!seenUserIds.has(formattedUser.id)) {
          seenUserIds.add(formattedUser.id);
          processedUsers.push(formattedUser);
        }
      });
    });

    if (!groups.length && Array.isArray(apiData.workers)) {
      addAgency(apiData);
      const agencyMapSingle = buildAgencyMap([apiData]);
      apiData.workers.forEach((worker) => {
        const formattedUser = formatUserForDisplay(worker, agencyMapSingle, {
          id: apiData.id,
          name: apiData.name,
        });
        if (!seenUserIds.has(formattedUser.id)) {
          seenUserIds.add(formattedUser.id);
          processedUsers.push(formattedUser);
        }
      });
    }

    return { agencies: processedAgencies, users: processedUsers };
  }

  return { agencies: [], users: [] };
};

export function Team() {
  const { user: currentUser, interceptorReady, refreshUser } = useAuth();
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    refreshFilterData,
    teamRefreshKey,
    filterRefresh,
  } = useOutletContext();
  const { colors, neutrals } = useTheme();
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [isCreateAgencyOpen, setIsCreateAgencyOpen] = useState(false);
  const [isEditAgencyOpen, setIsEditAgencyOpen] = useState(false);
  const [brandingAgency, setBrandingAgency] = useState(null);
  const [editingAgencyId, setEditingAgencyId] = useState(null);
  const [isDeleteAgencyOpen, setIsDeleteAgencyOpen] = useState(false);
  const [deletingAgencyId, setDeletingAgencyId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Agency pending archive/unarchive confirmation
  const [archivingAgency, setArchivingAgency] = useState(null);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);
  const [selectedAgencyDetails, setSelectedAgencyDetails] = useState(null);

  // Multi-agency assignment modal state
  const [isAssignAgencyOpen, setIsAssignAgencyOpen] = useState(false);
  const [assigningConsultant, setAssigningConsultant] = useState(null);

  const [newAgency, setNewAgency] = useState({
    name: "",
    location: "",
    additional_notes: "",
    phone: "",
    email: "",
    website: "",
    byneural_tenant_id: "",
  });
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [planLimitError, setPlanLimitError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [popup, setPopup] = useState({
    open: false,
    message: "",
    error: false,
  });

  // Backend data
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch ALL agencies for the multi-agency assignment modal (SuperAdmin only)
  // This is separate from the filtered agencies view to ensure modal always has complete list
  const { data: allAgenciesData } = getData(
    currentUser?.role === "superadmin" ? "agencies/" : null,
    interceptorReady && currentUser?.role === "superadmin",
    [refresh, teamRefreshKey]
  );

  // Process all agencies for the modal (simple list with id and name)
  const allAgenciesForModal = useMemo(() => {
    if (!allAgenciesData) return [];
    const agenciesList = Array.isArray(allAgenciesData)
      ? allAgenciesData
      : allAgenciesData?.results || [];
    return agenciesList.map(agency => ({
      id: agency.id,
      name: agency.name,
    }));
  }, [allAgenciesData]);

  // Determinar el endpoint según el rol del usuario y los filtros aplicados
  const getEndpoint = () => {
    if (currentUser?.role === "superadmin") {
      if (selectedFilter && selectedFilter !== "global") {
        return `agencies/${selectedFilter}/?expand=workers`;
      }
      return "agencies/?expand=workers";
    } else if (currentUser?.role === "agency_admin") {
      // Para agency_admin, NO aplicar filtro de cliente de AppLayout
      // Backend already filters by user's agency automatically
      return `users/?role=consultant,agency_admin&group_by=agency`;
    }
    return null;
  };

  // Get data from backend
  const {
    data: apiData,
    error: apiError,
    isError: apiIsError,
  } = getData(
    getEndpoint(),
    interceptorReady,
    currentUser?.role === "agency_admin"
      ? [refresh, teamRefreshKey] // Para agency_admin, no incluir selectedFilter
      : [refresh, selectedFilter, teamRefreshKey, filterRefresh] // Para superadmin, incluir selectedFilter
  );

  // Process backend data
  useEffect(() => {
    if (apiData && currentUser?.role) {
      const { agencies: processedAgencies, users: processedUsers } =
        normalizeTeamData(apiData, currentUser.role);

      setAgencies(processedAgencies);
      setUsers(processedUsers);
      setLoading(false);
    } else if (apiIsError) {
      console.error("Error loading data:", apiError);
      setLoading(false);
    }
  }, [
    apiData,
    apiIsError,
    apiError,
    currentUser?.role,
    ...(currentUser?.role === "superadmin" ? [selectedFilter] : []),
  ]);

  // Los miembros ya vienen filtrados del backend, no necesitamos filtrar por frontend
  const availableMembers =
    currentUser?.role === "superadmin" || currentUser?.role === "agency_admin"
      ? users
      : [];

  // Solo filtrar por búsqueda de texto; la API ya entrega usuarios filtrados correctamente
  // (incluye consultores invitados a la agencia del agency_admin)
  const filteredMembers = availableMembers.filter((member) => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const handleCreateAgency = async (payloadFromModal = null) => {
    const agencyPayload = payloadFromModal || newAgency;
    if (agencyPayload.name?.trim()) {
      try {
        const response = await apiClient.post("agencies/", {
          name: agencyPayload.name,
          location: agencyPayload.location || "",
          additional_notes: agencyPayload.additional_notes || "",
          phone: agencyPayload.phone || "",
          email: agencyPayload.email || "",
          website: agencyPayload.website || "",
          ...(agencyPayload.agency_type && { agency_type: agencyPayload.agency_type }),
        });

        if (response.data) {
          console.log("Nueva agencia creada:", response.data);
          setRefresh((prev) => prev + 1); // Refresh data
          if (refreshFilterData) refreshFilterData(); // Refresh filter data in AppLayout
          setIsCreateAgencyOpen(false);
          setNewAgency({
            name: "",
            location: "",
            additional_notes: "",
            phone: "",
            email: "",
            website: "",
            byneural_tenant_id: "",
          });
          // Mostrar popup de éxito
          setPopup({
            open: true,
            message: "Agencia creada exitosamente",
            error: false,
          });
        }
      } catch (error) {
        console.error("Error creando agencia:", error);
        // Mostrar popup de error
        const errorMessage =
          error.response?.data?.message || error.message || "Error desconocido";
        setPopup({ open: true, message: errorMessage, error: true });
      }
    }
  };

  const handleEditAgency = async (payloadFromModal = null) => {
    const agencyPayload = payloadFromModal || newAgency;
    if (!editingAgencyId || !agencyPayload.name?.trim()) {
      return;
    }

    try {
      // Preparar todos los campos editables (PATCH permite actualización parcial)
      const updateData = {
        name: agencyPayload.name,
        location: agencyPayload.location || "",
        additional_notes: agencyPayload.additional_notes || "",
        phone: agencyPayload.phone || "",
        email: agencyPayload.email || "",
        website: agencyPayload.website || "",
        byneural_tenant_id: agencyPayload.byneural_tenant_id || "",
        // Solo el superadmin puede cambiar el tipo de agencia
        ...(currentUser?.role === "superadmin" &&
          agencyPayload.agency_type && { agency_type: agencyPayload.agency_type }),
      };

      const response = await apiClient.patch(
        `agencies/${editingAgencyId}/`,
        updateData
      );

      if (response.data) {
        console.log("Agencia actualizada:", response.data);
        setRefresh((prev) => prev + 1); // Refresh data
        if (refreshFilterData) refreshFilterData(); // Refresh filter data in AppLayout
        setIsEditAgencyOpen(false);
        setEditingAgencyId(null);
        setNewAgency({
          name: "",
          location: "",
          additional_notes: "",
          phone: "",
          email: "",
          website: "",
          byneural_tenant_id: "",
        });
        // Mostrar popup de éxito
        setPopup({
          open: true,
          message: "Agencia actualizada exitosamente",
          error: false,
        });
      }
    } catch (error) {
      console.error("Error actualizando agencia:", error);
      // Mostrar popup de error
      const errorMessage =
        error.response?.data?.message || error.message || "Error desconocido";
      setPopup({ open: true, message: errorMessage, error: true });
    }
  };

  const handleOpenEditAgency = (agencyId) => {
    setEditingAgencyId(agencyId);
    setIsEditAgencyOpen(true);
  };

  const handleOpenDeleteAgency = (agencyId) => {
    setDeletingAgencyId(agencyId);
    setIsDeleteAgencyOpen(true);
  };

  const handleOpenAgencyDetails = (agency) => {
    setSelectedAgencyDetails(agency);
  };

  // Multi-agency assignment handlers
  const handleOpenAssignAgency = (consultant) => {
    setAssigningConsultant(consultant);
    setIsAssignAgencyOpen(true);
  };

  const handleAssignAgencySuccess = (action, responseData) => {
    // Refresh data after successful assignment/removal
    setRefresh((prev) => prev + 1);

    // Show success popup
    const message = action === "assign"
      ? "Consultor asignado a agencia exitosamente"
      : "Consultor removido de agencia exitosamente";
    setPopup({ open: true, message, error: false });

    // Update the consultant in the modal with new data
    // Backend returns { user: { agency: id, invited_agencies: [{id, name}] } }
    if (responseData?.user) {
      // Build agencies array from primary agency + invited_agencies
      const agencies = [];
      const user = responseData.user;

      // Add primary agency (need to find name from allAgenciesForModal)
      if (user.agency) {
        const primaryAgencyId = typeof user.agency === 'object' ? user.agency.id : user.agency;
        const primaryAgencyData = allAgenciesForModal.find(a => a.id === primaryAgencyId);
        if (primaryAgencyData) {
          agencies.push({ id: primaryAgencyData.id, name: primaryAgencyData.name });
        } else {
          agencies.push({ id: primaryAgencyId, name: `Agency ${primaryAgencyId}` });
        }
      }

      // Add invited agencies
      if (user.invited_agencies && Array.isArray(user.invited_agencies)) {
        user.invited_agencies.forEach(invitedAgency => {
          if (!agencies.some(a => a.id === invitedAgency.id)) {
            agencies.push({ id: invitedAgency.id, name: invitedAgency.name });
          }
        });
      }

      setAssigningConsultant({
        ...assigningConsultant,
        agencies: agencies,
      });
    }
  };

  const handleToggleArchiveAgency = async () => {
    const agency = archivingAgency;
    const action = agency.isArchived ? "unarchive" : "archive";
    setIsTogglingArchive(true);
    try {
      await apiClient.post(`agencies/${agency.id}/${action}/`);
      setRefresh((prev) => prev + 1);
      if (refreshFilterData) refreshFilterData();
      setPopup({
        open: true,
        message: agency.isArchived ? "Agencia desarchivada" : "Agencia archivada",
        error: false,
      });
    } catch (error) {
      setPopup({
        open: true,
        message:
          error.response?.data?.detail ||
          (agency.isArchived ? "No se pudo desarchivar la agencia" : "No se pudo archivar la agencia"),
        error: true,
      });
    }
    setIsTogglingArchive(false);
    setArchivingAgency(null);
  };

  const handleDeleteAgency = async () => {
    if (!deletingAgencyId) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await apiClient.delete(`agencies/${deletingAgencyId}/`);

      if (response.status === 200 || response.status === 204) {
        console.log("Agencia eliminada:", deletingAgencyId);
        setRefresh((prev) => prev + 1); // Refresh data
        if (refreshFilterData) refreshFilterData(); // Refresh filter data in AppLayout
        setIsDeleteAgencyOpen(false);
        setDeletingAgencyId(null);
        // Mostrar popup de éxito
        setPopup({
          open: true,
          message: "Agencia eliminada exitosamente",
          error: false,
        });
      }
    } catch (error) {
      console.error("Error eliminando agencia:", error);
      // Mostrar popup de error
      const errorMessage =
        error.response?.data?.message || error.message || "Error desconocido";
      setPopup({ open: true, message: errorMessage, error: true });
    } finally {
      setIsDeleting(false);
    }
  };

  // Función auxiliar para copiar al portapapeles compatible con Safari
  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      textArea.remove();
      return true;
    } catch (error) {
      console.error("Fallback: No se pudo copiar", error);
      textArea.remove();
      return false;
    }
  };

  const copyToClipboard = async (text) => {
    // Intentar primero con la API moderna del portapapeles
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        return copyToClipboardFallback(text);
      }
    } else {
      // Usar método fallback para navegadores que no soportan la API o contextos no seguros
      return copyToClipboardFallback(text);
    }
  };

  const handleGenerateInviteLink = async (role) => {
    try {
      const endpoint =
        role === "admin" ? "generate_admin_invites" : "generate_consultant_invites";
      const response = await apiClient.post(
        `agencies/${currentUser?.agencyId}/${endpoint}/`
      );
      return response.data.url;
    } catch (error) {
      console.error("Error generando enlace:", error);
      const limitError = getPlanLimitError(error);
      if (limitError?.kind === "users") {
        setIsInviteOpen(false);
        setPlanLimitError(limitError);
      }
      return null;
    }
  };

  const [togglingActive, setTogglingActive] = useState(false);
  // Un consultor invitado desde otra agencia se gestiona en la suya: desactivarlo lo dejaría fuera de las dos
  const canToggleActive = (member) =>
    member.id !== currentUser?.id &&
    (currentUser?.role === "superadmin" ||
      (currentUser?.role === "agency_admin" && member.agencyId === currentUser.agencyId));

  const handleToggleActive = async (member) => {
    const activate = member.status === "inactive";
    setTogglingActive(true);
    try {
      await apiClient.patch(`users/${member.id}/`, { is_active: activate });
      setSelectedMember({ ...member, status: activate ? "active" : "inactive" });
      setRefresh((prev) => prev + 1);
      await refreshUser();
      setPopup({
        open: true,
        message: activate ? `${member.name} vuelve a tener acceso.` : `${member.name} ya no puede entrar y su plaza queda libre.`,
        error: false,
      });
    } catch (error) {
      const limitError = getPlanLimitError(error);
      if (limitError) {
        setSelectedMember(null);
        setPlanLimitError(limitError);
      } else {
        setPopup({ open: true, message: error.response?.data?.detail || "No se ha podido guardar el cambio.", error: true });
      }
    } finally {
      setTogglingActive(false);
    }
  };

  const agencyPlan = currentUser?.agencyPlan;
  const userLimit = currentUser?.role === "agency_admin" && agencyPlan ? agencyPlan.users_limit : null;
  const userLimitReached = userLimit !== null && !agencyPlan.can_add_users;

  const getHeaderInfo = () => {
    switch (currentUser?.role) {
      case "superadmin":
        return {
          title: "Gestión de Equipo",
          subtitle: selectedFilter
            ? "Administra el equipo de la agencia seleccionada"
            : "Administra todas las agencias y consultores",
        };
      case "agency_admin":
        return {
          title: "Mi Equipo",
          subtitle: "Gestiona los consultores y administradores de tu agencia",
        };
      default:
        return {
          title: "Equipo",
          subtitle: "Información del equipo",
        };
    }
  };

  const headerInfo = getHeaderInfo();

  // Set page context
  useEffect(() => {
    if (currentUser) {
      const rightContent = (
        <Stack direction="row" spacing={2} alignItems="center">
          {currentUser.role === "superadmin" && (
            <Button
              onClick={() => setIsCreateAgencyOpen(true)}
              sx={{ gap: 1 }}
            >
              <Plus size={16} />
              Nueva Agencia
            </Button>
          )}

          {currentUser.role === "agency_admin" && (
            <Button
              onClick={() => {
                // Con el límite alcanzado el botón vende el upgrade en vez de generar un enlace que fallará
                if (userLimitReached) {
                  setPlanLimitError({ kind: "users", limit: userLimit });
                  return;
                }
                setIsInviteOpen(true);
              }}
              sx={{ gap: 1 }}
            >
              <UserPlus size={16} />
              Invitar usuario
            </Button>
          )}
        </Stack>
      );

      // Breadcrumb para superadmin
      let breadcrumbItems = null;
      if (currentUser.role === "superadmin") {
        if (selectedFilter && selectedFilterData) {
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Agencia: ${selectedFilterData.name}` },
          ];
        } else {
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      }

      const pageContextObj = {
        title: headerInfo.title,
        subtitle: headerInfo.subtitle,
        showTitle: true,
        rightContent: rightContent,
        titleRightContent:
          userLimit !== null ? (
            <Badge color={userLimitReached ? "pending" : "muted"} dot={false}>
              {agencyPlan.users_count}/{userLimit} usuarios
            </Badge>
          ) : undefined,
      };

      if (breadcrumbItems) {
        pageContextObj.breadcrumbItems = breadcrumbItems;
      }

      setPageContext(pageContextObj);
    }
  }, [
    currentUser,
    setPageContext,
    headerInfo.title,
    headerInfo.subtitle,
    selectedFilter,
    selectedFilterData,
    userLimit,
    userLimitReached,
    agencyPlan?.users_count,
  ]);

  const memberSearch = (
    <CustomTextField
      placeholder="Buscar miembros..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{ width: "100%", maxWidth: "260px" }}
      isSearch={true}
    />
  );

  // Estadísticas del equipo (simplified for now)
  const teamStats = {
    totalMembers: availableMembers.length,
    activeMembers: availableMembers.filter((m) => m.status === "active").length,
    consultants: availableMembers.filter((m) => m.role === "Consultor").length,
    admins: availableMembers.filter((m) => m.role.includes("Admin")).length,
  };

  // Superadmin: KPIs separadas por agencias Serseo / No Serseo (según la agencia principal del miembro)
  const agencyTypeById = new Map(agencies.map((agency) => [agency.id, agency.agencyType]));
  const isExternalAgency = (agencyId) => agencyTypeById.get(agencyId) === "external";
  const splitBySerseo = (members) => ({
    serseo: members.filter((m) => !isExternalAgency(m.agencyId)).length,
    external: members.filter((m) => isExternalAgency(m.agencyId)).length,
  });
  const splitByArchived = (agencyList) => ({
    active: agencyList.filter((agency) => !agency.isArchived).length,
    archived: agencyList.filter((agency) => agency.isArchived).length,
  });
  const superadminStats = {
    serseoAgencies: splitByArchived(agencies.filter((agency) => agency.agencyType !== "external")),
    externalAgencies: splitByArchived(agencies.filter((agency) => agency.agencyType === "external")),
    consultants: splitBySerseo(availableMembers.filter((m) => m.role === "Consultor")),
    admins: splitBySerseo(availableMembers.filter((m) => m.role.includes("Admin"))),
  };
  const renderSerseoSplit = ({ serseo, external }) => (
    <Typography variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
      {serseo} Serseo · {external} No Serseo
    </Typography>
  );

  const selectedAgencyDetailsData = selectedAgencyDetails
    ? agencies.find((agency) => agency.id === selectedAgencyDetails.id) ||
    selectedAgencyDetails
    : null;

  return (
    <Stack spacing={3}>
      {/* Estadísticas del equipo */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md:
              currentUser?.role === "superadmin"
                ? "repeat(4, 1fr)"
                : "repeat(3, 1fr)",
          },
        }}
      >
        {currentUser?.role === "superadmin" ? (
          <>
            <MetricCard
              icon={Building2}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={superadminStats.serseoAgencies.active}
              label="Agencias Serseo"
              isLoading={loading}
              valueAdornment={<ArchivedCount count={superadminStats.serseoAgencies.archived} />}
            />

            <MetricCard
              icon={Building2}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={superadminStats.externalAgencies.active}
              label="Agencias No Serseo"
              isLoading={loading}
              valueAdornment={<ArchivedCount count={superadminStats.externalAgencies.archived} />}
            />

            <MetricCard
              icon={UserPlus}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={teamStats.consultants}
              label="Consultores"
              isLoading={loading}
              valueAdornment={renderSerseoSplit(superadminStats.consultants)}
            />

            <MetricCard
              icon={Settings}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={teamStats.admins}
              label="Administradores"
              isLoading={loading}
              valueAdornment={renderSerseoSplit(superadminStats.admins)}
            />
          </>
        ) : (
          <>
            <MetricCard
              icon={Users}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={teamStats.totalMembers}
              label="Total Miembros"
              isLoading={loading}
            />

            <MetricCard
              icon={CheckCircle}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={teamStats.activeMembers}
              label="Miembros Activos"
              isLoading={loading}
            />

            <MetricCard
              icon={UserPlus}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={teamStats.consultants}
              label="Consultores"
              isLoading={loading}
            />
          </>
        )}
      </Box>

      {/* Lista de miembros del equipo */}
      <Stack spacing={2}>
        {loading ? (
          <Stack spacing={2}>
            {Array.from({
              length: currentUser?.role === "superadmin" ? 2 : 1,
            }).map((_, index) => (
              <Card key={`team-skeleton-${index}`}>
                <CardContent>
                  <Stack spacing={2}>
                    <Skeleton variant="text" width="35%" height={22} />
                    {Array.from({ length: 3 }).map((__, memberIndex) => (
                      <Skeleton
                        key={`member-skeleton-${index}-${memberIndex}`}
                        variant="rounded"
                        height={56}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {currentUser?.role === "superadmin" ? (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      fontSize: "1rem",
                    }}
                  >
                    Agencias
                  </Typography>
                  {memberSearch}
                </Stack>
                {(searchTerm.trim() === ""
                  ? agencies
                  : agencies.filter((agency) =>
                    filteredMembers.some(
                      (member) => member.agencies?.some(a => a.id === agency.id)
                    )
                  )
                ).map((agency) => {
                  const agencyMembers = filteredMembers
                    .filter((m) => m.agencies?.some(a => a.id === agency.id))
                    .sort((a, b) => {
                      // Admins first, then consultants
                      if (a.role === "Admin de Agencia" && b.role !== "Admin de Agencia") return -1;
                      if (a.role !== "Admin de Agencia" && b.role === "Admin de Agencia") return 1;
                      return 0;
                    });

                  return (
                    <AgencyCard
                      key={agency.id}
                      agency={agency}
                      members={agencyMembers}
                      onMemberClick={setSelectedMember}
                      onEditClick={handleOpenEditAgency}
                      onBrandingClick={setBrandingAgency}
                      onDeleteClick={handleOpenDeleteAgency}
                      onToggleArchive={setArchivingAgency}
                      onViewClientStats={handleOpenAgencyDetails}
                      onAssignAgency={handleOpenAssignAgency}
                      showAgencyBadges={true}
                      isSuperAdmin={currentUser?.role === "superadmin"}
                    />
                  );
                })}
              </>
            ) : currentUser?.role === "agency_admin" ? (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: "1rem",
                  }}
                ></Typography>
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      {memberSearch}
                      {[...filteredMembers]
                        .sort(
                          (a, b) =>
                            (b.role === "Admin de Agencia") -
                            (a.role === "Admin de Agencia")
                        )
                        .map((member) => (
                          <TeamMemberCard
                            key={member.id}
                            member={member}
                            onMemberClick={setSelectedMember}
                          />
                        ))}
                      {filteredMembers.length === 0 && (
                        <Typography color="text.secondary" textAlign="center">
                          No hay miembros en tu agencia
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </Stack>
        )}
      </Stack>

      {/* Modales */}
      <MemberModal
        member={selectedMember}
        agency={
          selectedMember
            ? agencies.find((a) => a.id === selectedMember.agencyId)?.name
            : ""
        }
        open={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        canManageClients={["superadmin", "agency_admin"].includes(currentUser?.role)}
        onNotify={(message, error) => setPopup({ open: true, message, error })}
        onToggleActive={selectedMember && canToggleActive(selectedMember) ? handleToggleActive : undefined}
        togglingActive={togglingActive}
      />

      <AgencyModal
        open={isCreateAgencyOpen}
        onClose={() => {
          setIsCreateAgencyOpen(false);
          setNewAgency({
            name: "",
            location: "",
            additional_notes: "",
            phone: "",
            email: "",
            website: "",
            byneural_tenant_id: "",
          });
        }}
        agency={newAgency}
        onSave={handleCreateAgency}
        onAgencyChange={setNewAgency}
        mode="create"
      />

      <UnifiedModal
        open={!!brandingAgency}
        onClose={() => setBrandingAgency(null)}
        title={brandingAgency ? `Marca de ${brandingAgency.name}` : "Marca"}
        maxWidth="md"
      >
        {brandingAgency && <AgencyBrandingSettings agencyId={brandingAgency.id} />}
      </UnifiedModal>

      <AgencyModal
        open={isEditAgencyOpen}
        onClose={() => {
          setIsEditAgencyOpen(false);
          setEditingAgencyId(null);
          setNewAgency({
            name: "",
            location: "",
            additional_notes: "",
            phone: "",
            email: "",
            website: "",
            byneural_tenant_id: "",
          });
        }}
        agency={newAgency}
        onSave={handleEditAgency}
        onAgencyChange={setNewAgency}
        mode="edit"
        agencyId={editingAgencyId}
      />

      <SuccessPopup
        message={popup.message}
        isOpen={popup.open}
        onClose={() => setPopup({ open: false, message: "", error: false })}
        error={popup.error}
      />

      <AgencyClientStatsModal
        agency={selectedAgencyDetailsData}
        open={!!selectedAgencyDetailsData}
        onClose={() => setSelectedAgencyDetails(null)}
      />

      {/* Modal para asignar consultores a agencias (multi-agency) */}
      {currentUser?.role === "superadmin" && (
        <AssignAgencyModal
          open={isAssignAgencyOpen}
          onClose={() => {
            setIsAssignAgencyOpen(false);
            setAssigningConsultant(null);
          }}
          consultant={assigningConsultant}
          allAgencies={allAgenciesForModal}
          onSuccess={handleAssignAgencySuccess}
        />
      )}

      <InviteUserModal
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onGenerate={handleGenerateInviteLink}
        onCopy={copyToClipboard}
      />

      {/* Modal de confirmación para archivar/desarchivar agencia */}
      <ConfirmModal
        open={Boolean(archivingAgency)}
        onClose={() => setArchivingAgency(null)}
        onConfirm={handleToggleArchiveAgency}
        title={`${archivingAgency?.isArchived ? "Desarchivar" : "Archivar"} agencia: ${archivingAgency?.name || ""}`}
        question={
          archivingAgency?.isArchived
            ? "¿Quieres desarchivar esta agencia?"
            : "¿Quieres archivar esta agencia?"
        }
        description={
          archivingAgency?.isArchived ? (
            <>
              <strong>{archivingAgency?.name}</strong> volverá a recibir anuncios y dejará de
              aparecer como archivada.
            </>
          ) : (
            <>
              <strong>{archivingAgency?.name}</strong> conservará todos sus datos, pero dejará de
              recibir anuncios. Puedes desarchivarla cuando quieras.
            </>
          )
        }
        tone={archivingAgency?.isArchived ? "info" : "warning"}
        confirmLabel={archivingAgency?.isArchived ? "Desarchivar agencia" : "Archivar agencia"}
        loadingLabel={archivingAgency?.isArchived ? "Desarchivando..." : "Archivando..."}
        loading={isTogglingArchive}
      />

      <PlanLimitDialog
        open={Boolean(planLimitError)}
        onClose={() => setPlanLimitError(null)}
        kind={planLimitError?.kind || "users"}
        planId={planLimitError?.plan}
        planName={planLimitError?.planName}
        limit={planLimitError?.limit}
      />

      {/* Modal de confirmación para eliminar agencia */}
      <ConfirmModal
        open={isDeleteAgencyOpen}
        onClose={() => {
          setIsDeleteAgencyOpen(false);
          setDeletingAgencyId(null);
        }}
        onConfirm={handleDeleteAgency}
        title={`Eliminar agencia: ${agencies.find((a) => a.id === deletingAgencyId)?.name || ""}`}
        question="¿Estás seguro de que quieres eliminar esta agencia?"
        description={
          <>
            Esta acción no se puede deshacer. Se eliminará permanentemente la agencia{" "}
            <strong>{agencies.find((a) => a.id === deletingAgencyId)?.name || ""}</strong> y toda
            su información asociada, incluyendo todos los miembros y consultores.
          </>
        }
        confirmLabel="Eliminar agencia"
        loadingLabel="Eliminando..."
        loading={isDeleting}
        tone="danger"
      />
    </Stack>
  );
}
